import React, { Component } from 'react';

import APIDocsCurl from 'common/developers/docs/api/APIDocsCurl';
import APIDocsMethod from 'common/developers/docs/api/APIDocsMethod';
import Highlight from 'common/developers/docs/api/Highlight';
import devURL from 'common/util/devURL';

import APIDocsBlock from '../APIDocsBlock';
import APIDocsTable from '../APIDocsTable';

export default class APIDocsChangePostStatusBlock extends Component {
  render() {
    const { company } = this.props;
    const statusURL = devURL(
      `https://${company?.subdomain}.canny.io/admin/settings/roadmap/statuses`,
    );
    return (
      <APIDocsBlock>
        <div>
          <div className="heading">Change post status</div>
          <div className="text">
            Changes a post's status (eg. to 'planned'), specified by its id.
          </div>
          <APIDocsTable
            title="Arguments"
            rows={[
              {
                description: 'Your secret API key.',
                heading: 'apiKey',
                subheading: 'string',
              },
              {
                description:
                  "The identifier of the admin to record as having changed the post's status. This will be visible in the post's activity section.",
                heading: 'changerID',
                subheading: 'string',
              },
              {
                description: "The post's unique identifier.",
                heading: 'postID',
                subheading: 'string',
              },
              {
                description: 'Whether or not to notify non-admin voters of the status change.',
                heading: 'shouldNotifyVoters',
                subheading: 'boolean',
              },
              {
                description: (
                  <div>
                    The status to change the post to. Options include: "open", "under review",
                    "planned", "in progress", "complete", "closed", or any other status your team
                    has set on the{' '}
                    {company?.subdomain ? (
                      <a href={statusURL}>settings&nbsp;page.</a>
                    ) : (
                      'settings\u00a0page.'
                    )}
                  </div>
                ),
                heading: 'status',
                subheading: 'string',
              },
              {
                description:
                  'The comment attached to this status change. If the comment includes line breaks, use "\n" instead of directly inputting the line breaks.',
                heading: 'commentValue',
                subheading: 'string',
              },
              {
                description:
                  'An array of the URLs of the images associated with this status change.',
                heading: 'commentImageURLs',
                subheading: 'array',
              },
            ]}
          />
          <div className="subheading">Returns</div>
          <div className="text">
            Returns the updated post if the post's status was successfully changed.
          </div>
          <div className="text">
            <div className="emphasize">Note:</div> If you try to change a post's status to its
            current status (eg. "planned" to "planned"), the endpoint will still respond with the
            updated post but none of the side-effects will trigger (post activity unit, slack
            notification, user notification, etc).
          </div>
        </div>
        <div>
          <div className="text">Endpoint</div>
          <Highlight lang="http">https://canny.io/api/v1/posts/change_status</Highlight>
          <APIDocsMethod />
          <APIDocsCurl
            path="https://canny.io/api/v1/posts/change_status"
            body={`\\
    -d apiKey=YOUR_API_KEY \\
    -d changerID=553c3ef8b8cdcd1501ba123a \\
    -d postID=553c3ef8b8cdcd1501ba1238 \\
    -d shouldNotifyVoters=true \\
    -d status="in progress"`}
          />
          <div className="text">Example Responses</div>
          <Highlight lang="json">
            {`{
  "id": "553c3ef8b8cdcd1501ba1238",
  "author": {
    "id": "553c3ef8b8cdcd1501ba123a",
    "created": "2017-07-15T22:11:00.000Z",
    "email": "test@test.test",
    "isAdmin": false,
    "name": "Sally Doe",
    "url": "https://your-company.canny.io/admin/users/sally-doe",
    "userID": "1234"
  },
  "board": {
    "created": "2017-07-10T11:22:00.000Z",
    "id": "553c3ef8b8cdcd1501ba1234",
    "name": "Feature Requests",
    "postCount": 123,
    "url": "https://your-company.canny.io/admin/board/feature-requests"
  },
  "by": {
    "id": "524c3ef8b8cdcd1501ba246b",
    "created": "2017-07-15T22:11:00.000Z",
    "email": "test@john.test",
    "isAdmin": true,
    "name": "John Doe",
    "url": "https://your-company.canny.io/admin/users/john-doe",
    "userID": "5678"
  },
  "category": {
    "id": "553c3ef8b8cdcd1501ba2234",
    "name": "Dashboard",
    "postCount": 42,
    "parentID": null,
    "url": "https://your-company.canny.io/admin/board/feature-requests?category=dashboard"
  },
  "changeComment": {
    "value": "The status has changed!",
    "imageURLs": ["https://canny.io/images/93fc5808937760b82c3dc00aa5cd86b2.png"]
  },
  "commentCount": 10,
  "created": "2017-08-22T13:32:00.000Z",
  "details": "Test post details",
  "eta": "February 2020",
  "imageURLs": [
    "https://canny.io/images/93fc5808937760b82c3dc00aa5cd86b8.png",
    "https://canny.io/images/316e5600645b81e4be287a52d506dbfd.jpg"
  ],
  "jira": {
    "linkedIssues": [{
      "id": "123",
      "key": "ID-123",
      "url": "https://your-company.atlassian.net/browse/ID-123"
    }]
  },
  "linear": {
    "linkedIssueIDs": ["ID-123", "ID-345"],
  },
  "mergeHistory":[{
    "created":"2018-06-17T22:42:37.167Z",
    "post": {
      "created":"2018-06-17T22:42:00.797Z",
      "details":"Awesome feature post details",
      "id": "553c3ef8b8cdcd1501ba6789",
      "imageURLs":[],
      "title":"Another awesome feature request"
    }
  }],
  "score": 72,
  "status": "in progress",
  "statusChangedAt": "2017-08-24T23:22:00.000Z",
  "tags": [{
    "id": "553c3ef8b8cdcd1501ba3234",
    "name": "iOS",
    "postCount": 15,
    "url": "https://your-company.canny.io/admin/board/feature-requests?tags=ios"
  }],
  "title": "An awesome feature request",
  "url": "https://your-company.canny.io/admin/board/feature-requests/p/an-awesome-feature-request"
}`}
          </Highlight>
          <Highlight lang="json">{'{"error":"invalid post id"}'}</Highlight>
        </div>
      </APIDocsBlock>
    );
  }
}
